import { useState, useCallback } from "react";
import { requestUserLocation } from "@/lib/map/geolocation";
import { track } from "@/lib/analytics";

// ── State model ──
export type ExploreLocationState =
  | { type: "none" }
  | { type: "geo"; lat: number; lng: number }
  | { type: "city"; city: string; lat: number; lng: number };

// ── Dalarna cities with approx coordinates ──
export interface CityOption {
  name: string;
  lat: number;
  lng: number;
}

export const DALARNA_CITIES: CityOption[] = [
  { name: "Falun", lat: 60.6065, lng: 15.6355 },
  { name: "Borlänge", lat: 60.4858, lng: 15.4375 },
  { name: "Mora", lat: 61.0042, lng: 14.5430 },
  { name: "Leksand", lat: 60.7312, lng: 14.9983 },
  { name: "Rättvik", lat: 60.8854, lng: 15.1126 },
  { name: "Sälen", lat: 61.1568, lng: 13.2642 },
];

// ── localStorage cache ──
const CACHE_KEY = "explore_location";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function readCache(): ExploreLocationState {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { type: "none" };
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return { type: "none" };
    }
    if (parsed.type === "geo") return { type: "geo", lat: parsed.lat, lng: parsed.lng };
    if (parsed.type === "city") return { type: "city", city: parsed.city, lat: parsed.lat, lng: parsed.lng };
    return { type: "none" };
  } catch {
    return { type: "none" };
  }
}

function writeCache(state: ExploreLocationState) {
  try {
    if (state.type === "none") {
      localStorage.removeItem(CACHE_KEY);
    } else {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ...state, ts: Date.now() }));
    }
  } catch {
    // ignore
  }
}

/**
 * Explore location hook with three-state model.
 * Reads cache on mount. Never auto-prompts browser geolocation.
 */
export function useExploreLocation() {
  const [location, setLocation] = useState<ExploreLocationState>(readCache);
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);

  const requestGeo = useCallback(async () => {
    setLoading(true);
    setDenied(false);

    track("explore_location_request", { method: "geo" }, "explore");

    const result = await requestUserLocation();
    setLoading(false);

    if (result.error || !result.location) {
      setDenied(true);
      track("explore_location_denied", { error: result.error ?? "unknown" }, "explore");
      return false;
    }

    const state: ExploreLocationState = {
      type: "geo",
      lat: result.location.lat,
      lng: result.location.lng,
    };
    setLocation(state);
    writeCache(state);
    track("explore_location_set", { method: "geo" }, "explore");
    return true;
  }, []);

  const selectCity = useCallback((city: CityOption) => {
    const state: ExploreLocationState = {
      type: "city",
      city: city.name,
      lat: city.lat,
      lng: city.lng,
    };
    setLocation(state);
    writeCache(state);
    setDenied(false);
    track("explore_location_set", { method: "city", city: city.name }, "explore");
  }, []);

  const clear = useCallback(() => {
    setLocation({ type: "none" });
    writeCache({ type: "none" });
    setDenied(false);
    track("explore_location_clear", {} as Record<string, never>, "explore");
  }, []);

  // Convenience getters
  const isActive = location.type !== "none";
  const userLocation = isActive ? { lat: (location as any).lat, lng: (location as any).lng } : null;
  const label =
    location.type === "geo"
      ? "Din position"
      : location.type === "city"
      ? location.city
      : null;

  return {
    location,
    loading,
    denied,
    isActive,
    userLocation,
    label,
    requestGeo,
    selectCity,
    clear,
  };
}
